import React, { useState } from 'react';
import { ArrowUpRight, Eye, ImageIcon } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { classNames } from '../../utils/classNames';

const TemplateCardMeta = ({ project, compact = false }) => {
  const { t } = useI18n();
  const viewCount = typeof project.viewCount === 'number' ? project.viewCount : 0;
  const viewLabel = t('publicTemplatesViewCount', { count: viewCount });

  return (
    <div className={classNames('space-y-2.5', compact ? 'p-3' : 'p-4')}>
      <h3
        className={classNames(
          'line-clamp-2 font-semibold text-text',
          compact ? 'text-xs leading-5' : 'text-sm'
        )}
      >
        {project.title}
      </h3>

      <div className="flex items-center justify-between gap-2">
        <span
          className={classNames(
            'min-w-0 truncate font-medium text-text/85',
            compact ? 'text-[11px]' : 'text-xs'
          )}
        >
          {project.owner?.fullName || t('publicTemplatesUnknownAuthor')}
        </span>

        <span
          className={classNames(
            'inline-flex shrink-0 items-center gap-1 text-muted',
            compact ? 'text-[11px]' : 'text-xs'
          )}
        >
          <Eye size={compact ? 12 : 13} />
          {viewLabel}
        </span>
      </div>
    </div>
  );
};

export const PublicTemplateCard = ({ project, onClick, variant = 'default' }) => {
  const { t } = useI18n();
  const [coverFailed, setCoverFailed] = useState(false);
  const isMinimal = variant === 'minimal' || variant === 'catalog';
  const isCatalog = variant === 'catalog';
  const description = project.description?.trim() || t('newProjectHint');
  const showCover = Boolean(project.coverImageUrl) && !coverFailed;

  if (isMinimal) {
    return (
      <article
        className={classNames(
          'group flex h-full cursor-pointer flex-col overflow-hidden rounded-xl border transition',
          'border-border/80 bg-panel-strong/50 hover:border-accent/35 hover:shadow-sm'
        )}
        onClick={() => onClick(project)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onClick(project);
          }
        }}
        role="button"
        tabIndex={0}
      >
        <div
          className={classNames(
            'relative aspect-[16/10] shrink-0 overflow-hidden',
            'bg-accent-soft/25'
          )}
        >
          {showCover ? (
            <img
              alt=""
              className="h-full w-full object-cover object-top transition duration-300 group-hover:scale-[1.02]"
              loading="lazy"
              onError={() => setCoverFailed(true)}
              src={project.coverImageUrl}
            />
          ) : (
            <div className="flex h-full items-center justify-center text-muted/60">
              <ImageIcon size={24} />
            </div>
          )}
        </div>
        <TemplateCardMeta project={project} compact />
      </article>
    );
  }

  return (
    <article
      className="group glass-panel relative flex h-full cursor-pointer flex-col overflow-hidden rounded-[1.35rem] border border-border/80 bg-white/75 transition duration-300 hover:-translate-y-1 hover:border-accent/30 hover:shadow-floating dark:bg-slate-950/20"
      onClick={() => onClick(project)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onClick(project);
        }
      }}
      role="button"
      tabIndex={0}
    >
      <div className="relative aspect-[16/10] overflow-hidden bg-gradient-to-br from-slate-100 via-accent-soft/40 to-teal-100/50 dark:from-slate-900 dark:via-accent/10 dark:to-slate-900">
        {showCover ? (
          <img
            alt=""
            className="h-full w-full object-cover object-top transition duration-500 group-hover:scale-[1.03]"
            loading="lazy"
            onError={() => setCoverFailed(true)}
            src={project.coverImageUrl}
          />
        ) : (
          <div className="flex h-full flex-col items-center justify-center gap-2 text-muted/80">
            <ImageIcon size={28} />
            <span className="px-4 text-center text-xs font-medium">{project.title}</span>
          </div>
        )}

        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-slate-950/55 via-transparent to-transparent opacity-90" />
        <div className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-3 p-4">
          <h3 className="line-clamp-2 text-sm font-semibold text-white">{project.title}</h3>
          <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-white/20 bg-white/10 text-white backdrop-blur transition group-hover:bg-white/20">
            <ArrowUpRight size={15} />
          </span>
        </div>
      </div>

      <div className="flex flex-1 flex-col">
        <p className="line-clamp-2 px-4 pt-4 text-sm leading-6 text-muted">{description}</p>
        <div className="mt-auto border-t border-border/70">
          <TemplateCardMeta project={project} />
        </div>
      </div>
    </article>
  );
};
